import {TextInput} from '@primer/react'
import {CopyIcon} from '@primer/octicons-react'
import {copyText} from '@github-ui/copy-to-clipboard'
import {usePlaygroundState} from './playground-manager'
import type {PlaygroundRequestParameters} from './model-client'
import {useRoutePayload} from '@github-ui/react-core/use-route-payload'
import type {Model, ShowModelPayload} from '../../types'
import React, {lazy} from 'react'

const CodeMirror = lazy(() => import('@github-ui/code-mirror'))

function replaceKeywords(template: string, values: PlaygroundRequestParameters) {
  const regex = /\{(\w+)\}/g
  return template.replace(regex, (match, key) => values[key] ?? match)
}

function getLanguageExt(lang: 'rest' | 'python' | 'js' | string): string {
  const extensionMap: {[key in 'rest' | 'python' | 'js']: string} = {
    rest: 'sh',
    python: 'py',
    js: 'js',
  }

  return lang in extensionMap ? extensionMap[lang as keyof typeof extensionMap] : 'txt'
}

export function PlaygroundCodeSnippet({model}: {model: Model}) {
  const playgroundState = usePlaygroundState()
  const {playgroundUrl} = useRoutePayload<ShowModelPayload>()

  const {systemPrompt, parameters} = playgroundState

  // Note: Never directly modify state
  const enrichedParameters = Object.assign({}, parameters)

  enrichedParameters['system_message'] = systemPrompt || ''
  enrichedParameters['model_name'] = model.name
  enrichedParameters['model_endpoint'] = playgroundUrl
  const {selectedLanguage} = usePlaygroundState()
  const snippet = model.samples.code[selectedLanguage as keyof typeof model.samples.code]

  const extension = getLanguageExt(selectedLanguage)

  if (!snippet) {
    return null
  }

  // If Azure release snippets for all SDKs, we'll have to update
  // this and add a switcher dropdown
  let content = snippet.sdks['azure-ai-inference']?.code || snippet.sdks['curl']?.code || 'Code snippet not available'

  content = replaceKeywords(content, enrichedParameters)

  return (
    <div className="d-flex position-relative flex-column overflow-auto" style={{flex: 1}}>
      <TextInput.Action
        icon={CopyIcon}
        onClick={() => copyText(content)}
        aria-label="Copy to clipboard"
        className="position-absolute fgColor-muted"
        sx={{right: '8px', top: '8px', zIndex: 10}}
      />

      <React.Suspense fallback={<></>}>
        <CodeMirror
          fileName={`document.${extension}`}
          value={content}
          ariaLabelledBy={''}
          height="100%"
          spacing={{
            indentUnit: 2,
            indentWithTabs: false,
            lineWrapping: false,
          }}
          hideHelpUntilFocus={true}
          onChange={() => {}}
        />
      </React.Suspense>
    </div>
  )
}

try{ CodeMirror.displayName ||= 'CodeMirror' } catch {}
try{ PlaygroundCodeSnippet.displayName ||= 'PlaygroundCodeSnippet' } catch {}